/**
 * Mirrors the backend ErrorEnvelope contract. Every error response from the API
 * conforms to this shape (post big-bang sweep). The useApiError hook routes
 * envelopes to the right rendering surface based on `code`, `httpStatus`, and
 * `fieldErrors` length.
 */
export interface ApiErrorEnvelope {
  code: string;
  userMessage: string;
  nextStep: string | null;
  fieldErrors: ApiFieldError[];
  traceId: string;
  httpStatus: number;
  // Optional structured payload for failures that carry per-item details
  // (e.g. bulk-import per-row errors). Undefined/null for ordinary failures.
  data?: unknown;
}

/**
 * A single field-level validation error. The `field` path uses dot/bracket
 * notation (`customer.phone`, `items[2].quantity`) and matches form input names.
 */
export interface ApiFieldError {
  field: string;
  code: string;
  userMessage: string;
}

/** Stable codes the frontend special-cases for routing. Mirrors backend ErrorCodes. */
export const API_ERROR_CODES = {
  ValidationFailed: "VALIDATION_FAILED",
  ResourceNotFound: "RESOURCE_NOT_FOUND",
  Unauthorized: "UNAUTHORIZED",
  Forbidden: "FORBIDDEN",
  InternalError: "INTERNAL_ERROR",
  NetworkError: "NETWORK_ERROR",
  MaintenanceMode: "MAINTENANCE_MODE",
  LegacyError: "LEGACY_ERROR",
} as const;

/** Discriminator for a value that looks like an ApiErrorEnvelope. */
export const isApiErrorEnvelope = (x: unknown): x is ApiErrorEnvelope => {
  if (!x || typeof x !== "object") return false;
  const obj = x as Record<string, unknown>;
  return (
    typeof obj.code === "string" &&
    typeof obj.userMessage === "string" &&
    typeof obj.httpStatus === "number" &&
    Array.isArray(obj.fieldErrors)
  );
};
